import { vetor, type Vetor3 } from '../geometria/vetor3'
import type { PerfilVeiculo } from './perfilVeiculo'
import { rotuloLadoVeiculo } from './rotulosLado'

/**
 * Aberturas por onde a vítima pode ser retirada.
 *
 * O ângulo de retirada não é escolhido: ele resulta de ONDE a vítima está
 * sentada e de POR ONDE ela sai. Modelar a abertura como entidade própria é o
 * que permite o aluno perguntar "e se eu tirar por aquela porta?" e ver o
 * número mudar.
 */
/**
 * O que precisa ser feito no veículo antes de a saída existir.
 *
 * A mesma abertura não custa o mesmo nos dois perfis: no hatch levanta-se a
 * tampa do porta-malas, e o vidro sobe junto; no sedan a saída a 0° pela
 * traseira é inviável sem técnica específica: vidro fixo, prateleira e, atrás do
 * encosto, a divisória do porta-malas. Tratar as duas como equivalentes esconde
 * justamente a decisão que o aluno precisa tomar.
 */
export type TecnicaDeAcesso = 'nenhuma' | 'levantarTampa' | 'corteTampaETeto'

export interface RequisitoDeAcesso {
  tecnica: TecnicaDeAcesso
  texto: string
}

const SEM_REQUISITO: RequisitoDeAcesso = {
  tecnica: 'nenhuma',
  texto: 'Abertura já disponível: basta abrir ou remover a porta.',
}

export interface AberturaSaida {
  id: string
  nome: string
  /** rótulo curto para botão e rótulo 3D */
  curto: string
  /** peça do catálogo correspondente, para destacar na cena */
  idPeca: string
  /** ponto representativo da abertura: o centro do vão */
  ponto: Vetor3
  lado: 'esquerdo' | 'direito' | 'central'
  requisito: RequisitoDeAcesso
}

/** A saída pela traseira depende do volume traseiro do veículo. */
function requisitoDaTraseira(perfil: PerfilVeiculo): RequisitoDeAcesso {
  return perfil.volumeTraseiro === 'hatch'
    ? {
        tecnica: 'levantarTampa',
        texto:
          'No hatch a tampa do porta-malas leva o vidro traseiro junto: basta levantá-la e rebater os encostos do banco traseiro para a frente — o vão fica livre até a cabine.',
      }
    : {
        tecnica: 'corteTampaETeto',
        texto:
          'No sedan a retirada a 0° pelo porta-malas é inviável sem técnica específica: o vidro traseiro é fixo, a prateleira fecha a cabine e, atrás do encosto (que rebate para a frente), a divisória do porta-malas permanece no caminho. É preciso retirar o vidro e cortar ou remover esse suporte — e, se ainda não bastar, cortar a tampa e o teto.',
      }
}

/** Centro do vão de uma porta, na altura da linha de cintura. */
function centroDaPorta(
  perfil: PerfilVeiculo,
  faixa: { zDianteiro: number; zTraseiro: number },
  sinal: number,
): Vetor3 {
  return vetor(
    (sinal * perfil.largura) / 2,
    (perfil.topoSoleiraY + perfil.cinturaY) / 2,
    (faixa.zDianteiro + faixa.zTraseiro) / 2,
  )
}

export function aberturasDeSaida(perfil: PerfilVeiculo): AberturaSaida[] {
  return [
    {
      id: 'portaDianteiraEsquerda',
      nome: `Porta dianteira do ${rotuloLadoVeiculo('esquerdo')}`,
      curto: 'diant. esq.',
      idPeca: 'portaDianteiraEsquerda',
      ponto: centroDaPorta(perfil, perfil.portaDianteira, 1),
      lado: 'esquerdo',
      requisito: SEM_REQUISITO,
    },
    {
      id: 'portaTraseiraEsquerda',
      nome: `Porta traseira do ${rotuloLadoVeiculo('esquerdo')}`,
      curto: 'tras. esq.',
      idPeca: 'portaTraseiraEsquerda',
      ponto: centroDaPorta(perfil, perfil.portaTraseira, 1),
      lado: 'esquerdo',
      requisito: SEM_REQUISITO,
    },
    {
      id: 'portaDianteiraDireita',
      nome: `Porta dianteira do ${rotuloLadoVeiculo('direito')}`,
      curto: 'diant. dir.',
      idPeca: 'portaDianteiraDireita',
      ponto: centroDaPorta(perfil, perfil.portaDianteira, -1),
      lado: 'direito',
      requisito: SEM_REQUISITO,
    },
    {
      id: 'portaTraseiraDireita',
      nome: `Porta traseira do ${rotuloLadoVeiculo('direito')}`,
      curto: 'tras. dir.',
      idPeca: 'portaTraseiraDireita',
      ponto: centroDaPorta(perfil, perfil.portaTraseira, -1),
      lado: 'direito',
      requisito: SEM_REQUISITO,
    },
    {
      id: 'tampaTraseira',
      nome: 'Traseira (tampa removida)',
      curto: 'traseira',
      idPeca: 'tampaTraseira',
      ponto: vetor(0, perfil.cinturaY - 0.1, perfil.traseiraZ + 0.1),
      lado: 'central',
      requisito: requisitoDaTraseira(perfil),
    },
    {
      id: 'tetoRemovido',
      nome: 'Teto removido',
      curto: 'teto',
      idPeca: 'teto',
      ponto: vetor(0, perfil.cinturaY + 0.55, 0),
      lado: 'central',
      requisito: { tecnica: 'corteTampaETeto', texto: 'Saída pelo teto somente após corte e remoção controlada do teto.' },
    },
    {
      id: 'paraBrisa',
      nome: 'Para-brisa (saída frontal)',
      curto: 'para-brisa',
      idPeca: 'paraBrisa',
      ponto: vetor(0, perfil.cinturaY + 0.15, perfil.colunaA.zBase + 0.35),
      lado: 'central',
      requisito: {
        tecnica: 'corteTampaETeto',
        texto: 'Saída frontal didática (180° em oposição à traseira). Dúvida frequente dos alunos: "Se tirar pelo para-brisa é 0°?". Embora a linha sagital seja reta, a doutrina adota a saída traseira como referencial zero (0°), ficando a frontal em 180°. Na prática é inviável por choque físico massivo com o volante, painel e motor, além do risco do vidro.',
      },
    },
  ]
}

/**
 * A mesma abertura, com o ponto na altura em que o corpo realmente passa.
 *
 * O ponto do vão é escrito na linha de cintura, que serve para a vítima sentada
 * no veículo sobre as rodas. Deitada no teto do veículo capotado ela atravessa o
 * vão quase um metro acima disso, e usar a altura de cintura ali punha a direção
 * do puxão fora do plano da rota — o ângulo saía de uma geometria que a animação
 * não percorria.
 */
export function aberturaNaAltura(abertura: AberturaSaida, y: number): AberturaSaida {
  return { ...abertura, ponto: vetor(abertura.ponto.x, y, abertura.ponto.z) }
}

export function aberturaPorId(perfil: PerfilVeiculo, id: string): AberturaSaida {
  const encontrada = aberturasDeSaida(perfil).find((a) => a.id === id)
  if (!encontrada) throw new Error(`Abertura de saída desconhecida: ${id}`)
  return encontrada
}
